import React, { useMemo } from 'react';

function formatAmount(value) {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '0';

    return amount.toLocaleString('ko-KR', {
        maximumFractionDigits: 4
    });
}

function formatDate(value) {
    return String(value || '').slice(0, 10);
}

function formatDateTime(value) {
    return String(value || '').replace('T', ' ').slice(0, 19);
}

function isTodayDate(value) {
    const today = new Date();
    const todayText = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    return formatDate(value) === todayText;
}

function EmptyRow({ colSpan, text }) {
    return (
        <tr>
            <td className="usage-contents-empty-cell" colSpan={colSpan}>{text}</td>
        </tr>
    );
}

const UsageContentsPanels = ({ rows, monthValue }) => {
    const { transactions, dailyRows, chargeRows, summary } = useMemo(() => ({
        transactions: rows.filter((item) => item?.record_type === 'transaction'),
        dailyRows: rows.filter((item) => item?.record_type === 'daily'),
        chargeRows: rows.filter((item) => item?.record_type === 'charge'),
        summary: rows.find((item) => item?.record_type === 'summary') || {}
    }), [rows]);

    const [year = '', month = ''] = String(monthValue || '').split('-');
    const monthLabel = `${year.slice(2)}년 ${month}월 사용`;

    return (
        <div className="usage-contents-panels-scroll">
            <div className="usage-contents-panels">
                <section className="usage-contents-panel transactions">
                    <div className="usage-contents-panel-title">
                        <strong>거래내역</strong>
                        <span>{monthLabel}: {formatAmount(summary.monthly_used)}원</span>
                    </div>
                    <div className="usage-contents-table-wrap">
                        <table>
                            <thead>
                                <tr>
                                    <th>날짜</th>
                                    <th>포인트</th>
                                    <th>잔액</th>
                                    <th>구분</th>
                                    <th>파일명</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transactions.length === 0 ? (
                                    <EmptyRow colSpan={5} text="선택한 달의 거래내역이 없습니다." />
                                ) : transactions.map((item, index) => (
                                    <tr key={`transaction-${item.record_id}-${index}`}>
                                        <td className={isTodayDate(item.created_at) ? 'usage-contents-today' : ''}>
                                            {formatDateTime(item.created_at)}
                                        </td>
                                        <td className={Number(item.amount) < 0 ? 'amount-used' : 'amount-added'}>
                                            {formatAmount(item.amount)}
                                        </td>
                                        <td>{formatAmount(item.balance_after)}</td>
                                        <td>{item.ref_type || ''}</td>
                                        <td className="usage-contents-file-name" title={item.file_name || ''}>
                                            {item.file_name || ''}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>

                <section className="usage-contents-panel daily">
                    <div className="usage-contents-panel-title">
                        <strong>일별 사용통계</strong>
                    </div>
                    <div className="usage-contents-table-wrap">
                        <table>
                            <thead>
                                <tr>
                                    <th>날짜</th>
                                    <th>건수</th>
                                    <th>금액</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dailyRows.length === 0 ? (
                                    <EmptyRow colSpan={3} text="일별 사용통계가 없습니다." />
                                ) : dailyRows.map((item, index) => (
                                    <tr key={`daily-${item.record_id}-${index}`}>
                                        <td className={isTodayDate(item.created_at) ? 'usage-contents-today' : ''}>
                                            {formatDate(item.created_at)}
                                        </td>
                                        <td>{formatAmount(item.item_count)}</td>
                                        <td>{formatAmount(item.amount)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>

                <section className="usage-contents-panel charges">
                    <div className="usage-contents-panel-title">
                        <strong>충전내역</strong>
                        <span>최근 100건</span>
                    </div>
                    <div className="usage-contents-table-wrap">
                        <table>
                            <thead>
                                <tr>
                                    <th>날짜</th>
                                    <th>충전금액</th>
                                    <th>비고</th>
                                </tr>
                            </thead>
                            <tbody>
                                {chargeRows.length === 0 ? (
                                    <EmptyRow colSpan={3} text="충전내역이 없습니다." />
                                ) : chargeRows.map((item, index) => (
                                    <tr key={`charge-${item.record_id}-${index}`}>
                                        <td className={isTodayDate(item.created_at) ? 'usage-contents-today' : ''}>
                                            {formatDate(item.created_at)}
                                        </td>
                                        <td>{formatAmount(item.charge_price)}원</td>
                                        <td className="usage-contents-note" title={item.note || ''}>{item.note || ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="usage-contents-balance">
                        <div>
                            <span>누적충전</span>
                            <strong>{formatAmount(summary.total_charged)}</strong>
                        </div>
                        <div>
                            <span>누적사용</span>
                            <strong>{formatAmount(summary.total_used)}</strong>
                        </div>
                        <div>
                            <span>잔액</span>
                            <strong>{formatAmount(summary.balance)}</strong>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default UsageContentsPanels;
